import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Button, Input, Select, Textarea, Field, Switch, Segmented, SectionTitle } from '../components/ui';
import { PacingGrid, emptySlots, normalizeSlots, type Slots } from '../components/PacingGrid';
import { ImplementationCode, useBrand } from '../components/ImplementationCode';
import { DestinationSetPicker } from '../components/DestinationSetPicker';
import { CampaignGtmGenerator } from '../components/CampaignGtmGenerator';
import {ShareRuleEditor,parseShareRules} from '../components/CampaignShare';
import { BudgetChoice, PulseModel, useCampaignBudget, clickBudget, clickTarget, budgetError, usd, type BudgetAction } from '../components/CampaignBudget';
import { api, ApiError } from '../lib/api';
import { currentUser } from '../lib/auth';
import type { ApiResponse, ContextEnrichmentFields } from '../lib/types';

const TIMEZONES = ['America/Sao_Paulo', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'UTC'];

interface ClientOpt { id: number; name: string }

const EVENT_TYPES = ['home', 'search', 'category', 'product', 'cart', 'purchase'];
const METHODS = [
  { v: 'google_analytics', l: 'Google Analytics' },
  { v: 'default', l: 'redirect' },
  { v: 'iframe', l: 'iframe' },
  { v: 'link', l: 'link (criteo)' },
];
const CLICKTRACKERS = ['trackingredir.com/r', '302ad.com/r', '302link.com/r'];
const TIME_RANGES = ['1', '2', '5', '10', '20', '30'];
const CONTEXT_DEFAULTS: ContextEnrichmentFields = {
  page_origin: true, page_path: false, querystring: true,
  utm_source: true, utm_medium: true, utm_campaign: true, utm_content: false, utm_term: false,
  navigation_referrer: true, gclid_present: true, fbclid_present: true,
  query_mode: 'all', query_allowlist: [], query_exclusions: [],
};
const CONTEXT_OPTIONS: { key: keyof Pick<ContextEnrichmentFields,
  'page_origin' | 'page_path' | 'querystring' | 'utm_source' | 'utm_medium' | 'utm_campaign' |
  'utm_content' | 'utm_term' | 'navigation_referrer' | 'gclid_present' | 'fbclid_present'>; label: string }[] = [
  { key: 'page_origin', label: 'Page domain' },
  { key: 'page_path', label: 'Page path' },
  { key: 'querystring', label: 'Query parameters' },
  { key: 'utm_source', label: 'UTM source' },
  { key: 'utm_medium', label: 'UTM medium' },
  { key: 'utm_campaign', label: 'UTM campaign' },
  { key: 'utm_content', label: 'UTM content' },
  { key: 'utm_term', label: 'UTM term' },
  { key: 'navigation_referrer', label: 'Previous page referrer' },
  { key: 'gclid_present', label: 'Google click ID present' },
  { key: 'fbclid_present', label: 'Meta click ID present' },
];
function contextFields(value: unknown): ContextEnrichmentFields {
  let parsed = value;
  if (typeof value === 'string') { try { parsed = JSON.parse(value); } catch { parsed = {}; } }
  return { ...CONTEXT_DEFAULTS, ...(parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}) } as ContextEnrichmentFields;
}
function parameterNames(value: unknown): string[] {
  return String(value || '').split(/[,\n]/).map(name => name.trim()).filter(Boolean);
}

// ISO / Date -> yyyy-mm-dd (para o <input type=date>)
function toDateInput(v: unknown): string {
  if (!v) return '';
  const d = new Date(v as string);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
// yyyy-mm-dd -> m/d/yyyy (formato que o backend parseia)
function toBackendDate(v: string): string | null {
  if (!v) return null;
  const [y, m, d] = v.split('-');
  if (!y || !m || !d) return null;
  return `${Number(m)}/${Number(d)}/${y}`;
}

type Form = Record<string, unknown>;

// Categorias do First Click Gate (espelha firstClick.js do audience + SOURCE_META do CampaignView).
const FIRST_CLICK_CATS: { v: string; label: string; color: string }[] = [
  { v: 'google_ads', label: 'Google Ads', color: '#4285F4' },
  { v: 'meta_ads', label: 'Meta Ads', color: '#0866FF' },
  { v: 'paid', label: 'Other paid', color: '#f59e0b' },
  { v: 'organic_search', label: 'Organic search', color: '#22c55e' },
  { v: 'social', label: 'Social', color: '#a855f7' },
  { v: 'referral', label: 'Referral', color: '#14b8a6' },
  { v: 'direct', label: 'Direct', color: '#64748b' },
  { v: 'organic', label: 'Organic', color: '#94a3b8' },
];
function parseCatList(v: unknown): string[] {
  const valid = new Set(FIRST_CLICK_CATS.map((c) => c.v));
  if (Array.isArray(v)) return (v as string[]).filter((c) => valid.has(c));
  if (typeof v === 'string' && v.trim()) {
    try { const a = JSON.parse(v); return Array.isArray(a) ? a.filter((c: string) => valid.has(c)) : []; } catch { return []; }
  }
  return [];
}
// Lista freeform de sources específicos (utm_source ou host de referrer) a bloquear.
function parseSourceList(v: unknown): string[] {
  if (Array.isArray(v)) return (v as unknown[]).map((s) => String(s).trim()).filter(Boolean);
  if (typeof v === 'string' && v.trim()) {
    try { const a = JSON.parse(v); if (Array.isArray(a)) return a.map((s) => String(s).trim()).filter(Boolean); } catch { /* não é JSON: trata como texto separado por vírgula/linha */ }
    return v.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

const emptyForm: Form = {
  id: '', name: '', period_start: '', period_finish: '', status: 'on',
  campaign_client_id: '', campaign_type: 'retargeting',
  method: 'google_analytics', platform: '', clicktracker: 'trackingredir.com/r', pulse_click_relay: '',
  clicks: 0, max_attempt_percent: 0, max_attempts_per_minute: 100, max_share_pct: 5, share_rules: [],
  event_types: [...EVENT_TYPES], referrer_check: false, fraud_guard_enabled: false,
  context_enrichment_enabled: false, context_enrichment_fields: CONTEXT_DEFAULTS,
  context_allowlist_text: '', context_exclusions_text: '',
  firstclick_allow: [], firstclick_block: [], firstclick_block_sources: '',
  frequency: 3, frequency_hour: '', pageviews_min: 1, pageviews_max: 4,
  device: 'both', time_range: '', min_user_days: 0, destination_set_id: '',
  custom_cpc: '', campaign_js_id: '', campaign_js_extra_content: '',
  // branding
  primary_domain: '', target_url: '', site_type: '', total_sessions: '',
  session_time_min: '', session_time_max: '', session_time_p50: '',
  bounce_rate: '', bounce_max_seconds: '', pages_per_session_min: '', pages_per_session_max: '',
  branding_referers: '',
  isolation_reset_profile: true, isolation_clear_cookies: false, isolation_incognito: false,
  pacing_slots: emptySlots() as Slots, pacing_timezone: 'America/Sao_Paulo',
};

export default function CampaignForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  // Cliente (não-admin) tem permissão bem limitada: só nome e período.
  // O backend aplica o mesmo whitelist no /campaigns/save.
  const isAdmin = !!currentUser()?.isAdmin;
  const [clientLabel, setClientLabel] = useState<string | null>(null);
  const brand = useBrand(clientLabel);

  const [form, setForm] = useState<Form>(emptyForm);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [generatingGtm, setGeneratingGtm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const integrated = isAdmin && !!form.catalog_recommendation_id;
  const budget = useCampaignBudget(integrated ? String(form.catalog_recommendation_id) : undefined);
  const [budgetDraft,setBudgetDraft] = useState('');
  const [budgetDirty,setBudgetDirty] = useState(false);
  const [budgetChoice,setBudgetChoice] = useState(false);
  const dirtyFields=useRef(new Set<string>()),budgetEditRevision=useRef<string|undefined>(undefined);
  const effectiveCpc = Number((dirtyFields.current.has('custom_cpc')?form.custom_cpc:budget.data?.cpc_usd) || form.custom_cpc || budget.data?.cpc_usd || 0);
  useEffect(()=>{dirtyFields.current.clear();budgetEditRevision.current=undefined;setBudgetDirty(false);},[id]);
  useEffect(()=>{if(!budget.data||saving)return;const live=budget.data;setForm(f=>({...f,
    ...(!dirtyFields.current.has('max_attempt_percent')&&live.max_attempt_percent!=null?{max_attempt_percent:live.max_attempt_percent}:{}),
    ...(!dirtyFields.current.has('custom_cpc')&&live.custom_cpc!==undefined?{custom_cpc:live.custom_cpc??''}:{})
  }));},[budget.data,saving]);
  function markBudgetDirty(){if(!budgetEditRevision.current)budgetEditRevision.current=budget.data?.revision;setBudgetDirty(true);}
  useLayoutEffect(()=>{if(budget.data&&!budgetDirty){setBudgetDraft(Number(budget.data.daily_budget_usd).toFixed(2));setForm(f=>({...f,clicks:budget.data!.target_clicks}));}},[budget.data,budgetDirty]);
  function changeBudget(value:string){setBudgetDraft(value);markBudgetDirty();setForm(f=>({...f,clicks:clickTarget(value,effectiveCpc)}));}
  function changeClicks(value:string){setForm(f=>({...f,clicks:value}));if(integrated&&budget.data){setBudgetDraft(clickBudget(Number(value),effectiveCpc));markBudgetDirty();}}

  const set = (k: string, v: unknown) => {dirtyFields.current.add(k);setForm((f) => ({ ...f, [k]: v }));};
  const type = form.campaign_type as string;
  const context = form.context_enrichment_fields as ContextEnrichmentFields;
  const setContext = (key: keyof ContextEnrichmentFields, value: unknown) =>
    setForm(f => ({ ...f, context_enrichment_fields: { ...(f.context_enrichment_fields as ContextEnrichmentFields), [key]: value } }));

  useEffect(() => {
    if (!isAdmin) return;
    api.get<ApiResponse<ClientOpt[]>>('/clients/list').then((r) => setClients(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get<ApiResponse<Record<string, unknown>>>(`/campaigns/get?id=${encodeURIComponent(id!)}`)
      .then((r) => {
        const c = r.data || {};
        setForm({
          ...emptyForm,
          ...c,
          period_start: toDateInput(c.period_start),
          period_finish: toDateInput(c.period_finish),
          status: c.status || 'on',
          referrer_check: c.referrer_check === 'on',
          fraud_guard_enabled: c.fraud_guard_enabled === true,
          context_enrichment_enabled: c.context_enrichment_enabled === true,
          context_enrichment_fields: contextFields(c.context_enrichment_fields),
          context_allowlist_text: contextFields(c.context_enrichment_fields).query_allowlist.join(', '),
          context_exclusions_text: contextFields(c.context_enrichment_fields).query_exclusions.join(', '),
          event_types: Array.isArray(c.event_types) ? c.event_types
            : (typeof c.event_types === 'string' ? JSON.parse(c.event_types as string) : [...EVENT_TYPES]),
          firstclick_allow: parseCatList(c.firstclick_allow),
          firstclick_block: parseCatList(c.firstclick_block),
          firstclick_block_sources: parseSourceList(c.firstclick_block_sources).join(', '),
          time_range: c.time_range != null ? String(c.time_range) : '',
          frequency_hour: c.frequency_hour != null ? String(c.frequency_hour) : '',
          destination_set_id: c.destination_set_id || '',
          campaign_client_id: c.campaign_client_id || '',
          custom_cpc: c.custom_cpc ?? '',
          pulse_click_relay: (c.pulse_click_relay as string) || '',
          campaign_js_id: (c.campaign_js_id as string) || '',
          campaign_js_extra_content: (c.campaign_js_extra_content as string) || '',
          pacing_slots: c.pacing_slots ? normalizeSlots(c.pacing_slots) : emptySlots(),
          pacing_timezone: (c.pacing_timezone as string) || 'America/Sao_Paulo',
        });
        // label do cliente da campanha → snippet da marca certa
        setClientLabel((c.client_label as string) || null);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  function toggleEvent(ev: string) {
    const cur = form.event_types as string[];
    set('event_types', cur.includes(ev) ? cur.filter((e) => e !== ev) : [...cur, ev]);
  }

  function toggleFC(field: 'firstclick_allow' | 'firstclick_block', cat: string) {
    const cur = (form[field] as string[]) || [];
    set(field, cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat]);
  }

  async function save(action?:BudgetAction) {
    if (generatingGtm) return;
    if(integrated){
      if(!budget.data){setError(budget.error||'Wait for the budget to load.');return;}
      if(!(Number(budgetDraft)>0)||!Number.isInteger(Number(form.clicks))||Number(form.clicks)<0){setError('Enter a positive budget and a non-negative click target.');return;}
      if(budgetDirty&&Number(budgetDraft)!==Number(budget.data.daily_budget_usd)&&!action){setBudgetChoice(true);return;}
    }
    setSaving(true);
    setError(null);
    const num = (v: unknown) => (v === '' || v == null ? null : Number(v));
    const payload: Record<string, unknown> = {
      id: form.id || undefined,
      original_id: isEdit ? id : undefined,
      name: form.name,
      period_start: toBackendDate(form.period_start as string),
      period_finish: toBackendDate(form.period_finish as string),
      status: form.status,
      campaign_id: form.campaign_id || form.id || undefined,
      campaign_client_id: form.campaign_client_id || null,
      campaign_type: type,
      custom_cpc: num(form.custom_cpc),
      method: form.method,
      platform: form.platform || null,
      clicktracker: form.clicktracker,
      // vazio = desligado: o /r redireciona direto ao destino
      pulse_click_relay: (form.pulse_click_relay as string)?.trim() || null,
      clicks: num(form.clicks),
      max_attempt_percent: num(form.max_attempt_percent),
      max_attempts_per_minute: num(form.max_attempts_per_minute),
      event_types: form.event_types,
      firstclick_allow: form.firstclick_allow || [],
      firstclick_block: form.firstclick_block || [],
      firstclick_block_sources: parseSourceList(form.firstclick_block_sources),
      referrer_check: form.referrer_check ? 'on' : 'off',
      fraud_guard_enabled: !!form.fraud_guard_enabled,
      frequency: num(form.frequency),
      frequency_hour: num(form.frequency_hour),
      pageviews_min: num(form.pageviews_min),
      pageviews_max: num(form.pageviews_max),
      device: form.device,
      time_range: form.time_range === '' ? (form.method === 'google_analytics' ? 0 : null) : num(form.time_range),
      min_user_days: num(form.min_user_days),
      destination_set_id: form.destination_set_id || null,
      campaign_js_id: form.campaign_js_id || null,
      campaign_js_extra_content: form.campaign_js_extra_content || null,
      items: [],
    };
    if(isEdit&&!dirtyFields.current.has('clicks'))delete payload.clicks;
    if (isAdmin) Object.assign(payload, {
      max_share_pct: num(form.max_share_pct),
      share_rules: parseShareRules(form.share_rules),
      context_enrichment_enabled: !!form.context_enrichment_enabled,
      context_enrichment_fields: {
        ...context,
        query_allowlist: parameterNames(form.context_allowlist_text),
        query_exclusions: parameterNames(form.context_exclusions_text),
      },
    });
    if (type === 'branding') {
      Object.assign(payload, {
        primary_domain: form.primary_domain || null,
        target_url: form.target_url || null,
        site_type: form.site_type || null,
        total_sessions: num(form.total_sessions),
        session_time_min: num(form.session_time_min),
        session_time_max: num(form.session_time_max),
        session_time_p50: num(form.session_time_p50),
        bounce_rate: form.bounce_rate === '' ? null : Number(form.bounce_rate) / 100,
        bounce_max_seconds: num(form.bounce_max_seconds),
        pages_per_session_min: num(form.pages_per_session_min),
        pages_per_session_max: num(form.pages_per_session_max),
        branding_referers: form.branding_referers || null,
        isolation_reset_profile: !!form.isolation_reset_profile,
        isolation_clear_cookies: !!form.isolation_clear_cookies,
        isolation_incognito: !!form.isolation_incognito,
        pacing_slots: JSON.stringify(form.pacing_slots || emptySlots()),
        pacing_timezone: form.pacing_timezone || 'America/Sao_Paulo',
      });
    }
    try {
      if(integrated&&budget.data&&budgetDirty){
        const updated=await budget.save({pixel_budget_daily_usd:budgetDraft,expected_revision:budgetEditRevision.current,...(action?{budget_action:action}:{})});
        payload.clicks=updated.target_clicks;
      }
      const r = await api.post<ApiResponse<unknown>>('/campaigns/save', payload);
      if (r.success) navigate('/campaigns');
      else setError(r.error || r.message || 'Failed to save');
    } catch (e) {
      setError(integrated ? budgetError(e) : e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Layout title="Campaign"><Card className="p-10 text-center text-slate-400">Loading…</Card></Layout>;
  }

  return (
    <Layout
      title={isEdit ? 'Edit Campaign' : 'Add Campaign'}
      actions={
        <>
          {isEdit && !!form.catalog_recommendation_id && <Button variant="outline" onClick={() => navigate(`/campaigns/recommendations/${id}`)}>Recommendations</Button>}
          <Button variant="outline" onClick={() => navigate('/campaigns')}>Cancel</Button>
          <Button onClick={()=>void save()} disabled={saving || generatingGtm || (integrated && !budget.data)}>{saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}</Button>
        </>
      }
    >
      {(error||budget.error) && <div role="alert" className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error||budget.error}</div>}
      {integrated&&<div className="mb-4"><PulseModel data={budget.data} /></div>}
      {budget.data&&<BudgetChoice data={budget.data} amount={budgetDraft} open={budgetChoice} busy={saving} error={error||budget.error} onCancel={()=>setBudgetChoice(false)} onChoose={action=>void save(action)} />}

      {/* Identidade */}
      <Card className="p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Name" className="md:col-span-2">
            <Input value={form.name as string} onChange={(e) => set('name', e.target.value)} placeholder="Campaign name" />
          </Field>
          {isAdmin && (
            <Field label="Client">
              <Select value={form.campaign_client_id as string} disabled={generatingGtm} onChange={(e) => set('campaign_client_id', e.target.value)}>
                <option value="">Select client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          )}
          <Field label="Status" help={isAdmin ? undefined : 'pause / resume the campaign'}>
            <div className="pt-1"><Switch checked={form.status === 'on'} onChange={(v) => set('status', v ? 'on' : 'off')} /></div>
          </Field>
          {isAdmin && (
            <Field label="Campaign Type" className="md:col-span-2">
              <Segmented value={type} onChange={(v) => set('campaign_type', v)} options={[{ value: 'retargeting', label: 'Retargeting' }, { value: 'branding', label: 'Branding' }]} />
            </Field>
          )}
          <Field label="Start">
            <Input type="date" value={form.period_start as string} onChange={(e) => set('period_start', e.target.value)} />
          </Field>
          <Field label="Finish">
            <Input type="date" value={form.period_finish as string} onChange={(e) => set('period_finish', e.target.value)} />
          </Field>
        </div>
      </Card>

      {!isAdmin && <DestinationSetPicker value={form.destination_set_id as string} onChange={(value) => set('destination_set_id', value)} />}

      {!isAdmin ? null : type === 'retargeting' ? (
        <>
          <Card className="p-5 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <SectionTitle>Delivery</SectionTitle>
              <Field label="Method">
                <Select value={form.method as string} onChange={(e) => set('method', e.target.value)}>
                  {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </Select>
              </Field>
              <Field label="Platform">
                <Select value={form.platform as string} onChange={(e) => set('platform', e.target.value)}>
                  <option value="">N/A</option>
                  <option value="vtex">vtex</option>
                </Select>
              </Field>
              <Field label="Click Tracker">
                <Select value={form.clicktracker as string} onChange={(e) => set('clicktracker', e.target.value)}>
                  {CLICKTRACKERS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Clicks (target)">
                <Input aria-label="Clicks target" type="number" min={integrated?1:0} disabled={integrated&&!budget.data} value={form.clicks as number} onChange={(e) => changeClicks(e.target.value)} />
              </Field>
              {integrated&&<Field label="Daily budget (USD)" help={budget.data ? `CPC ${usd(effectiveCpc)} · ${budget.data.cpc_source === 'client' ? 'Client default' : 'Campaign'}` : 'Loading budget…'}>
                <Input aria-label="Daily budget in USD" type="number" min="0.01" step="0.01" disabled={!budget.data} value={budgetDraft} onChange={e=>changeBudget(e.target.value)} />
              </Field>}
              <Field label="Click Relay URL" help="Optional. Chain the click through an external tracker (e.g. https://click.pulsedsp.com/c) before the final URL. Empty = redirect straight to the destination.">
                <Input type="url" placeholder="https://click.pulsedsp.com/c" value={(form.pulse_click_relay as string) || ''} onChange={(e) => set('pulse_click_relay', e.target.value)} />
              </Field>
            </div>
          </Card>

          <Card className="p-5 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <SectionTitle>Limits &amp; Audience</SectionTitle>
              <ShareRuleEditor maxShare={form.max_share_pct as string|number} rules={parseShareRules(form.share_rules)} onMaxShare={v=>set('max_share_pct',v)} onRules={v=>set('share_rules',v)} />
              <Field label="Max Attempt %" help="0–100">
                <Input aria-label="Max Attempt %" type="number" min={0} max={100} step={0.01} value={form.max_attempt_percent as number} onChange={(e) => set('max_attempt_percent', e.target.value)} />
              </Field>
              <Field label="Max Attempts/Min">
                <Input type="number" min={0} value={form.max_attempts_per_minute as number} onChange={(e) => set('max_attempts_per_minute', e.target.value)} />
              </Field>
              <Field label="Device">
                <Select value={form.device as string} onChange={(e) => set('device', e.target.value)}>
                  <option value="both">Both</option>
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                </Select>
              </Field>
              <Field label="Time Range (min)">
                <Select value={form.time_range as string} onChange={(e) => set('time_range', e.target.value)}>
                  <option value="">Auto / Deactivated</option>
                  {TIME_RANGES.map((t) => <option key={t} value={t}>{t} min</option>)}
                </Select>
              </Field>

              <Field label="Event Types" className="md:col-span-2">
                <div className="flex flex-wrap gap-3 pt-1">
                  {EVENT_TYPES.map((ev) => (
                    <label key={ev} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <input type="checkbox" checked={(form.event_types as string[]).includes(ev)} onChange={() => toggleEvent(ev)} />
                      {ev}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Frequency / User" help="per period (hours)">
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} value={form.frequency as number} onChange={(e) => set('frequency', e.target.value)} />
                  <span className="text-xs text-slate-400">every</span>
                  <Input type="number" min={0} value={form.frequency_hour as string} onChange={(e) => set('frequency_hour', e.target.value)} placeholder="0" />
                  <span className="text-xs text-slate-400">h</span>
                </div>
              </Field>
              <Field label="Pageviews (min / max)">
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} value={form.pageviews_min as number} onChange={(e) => set('pageviews_min', e.target.value)} />
                  <span className="text-slate-300">–</span>
                  <Input type="number" min={0} value={form.pageviews_max as number} onChange={(e) => set('pageviews_max', e.target.value)} />
                </div>
              </Field>

              <Field label="Min User Days" help="0–30">
                <Input type="number" min={0} max={30} value={form.min_user_days as number} onChange={(e) => set('min_user_days', e.target.value)} />
              </Field>
              <Field label="Custom CPC ($)" help="empty = client tier">
                <Input aria-label="Custom CPC" type="number" min={0} step={0.0001} value={form.custom_cpc as string} onChange={(e) => {set('custom_cpc', e.target.value);if(integrated&&budget.data)set('clicks',clickTarget(budgetDraft,e.target.value||budget.data.cpc_usd));}} placeholder="Client default" />
              </Field>
              <div className="flex items-end gap-6 md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!form.referrer_check} onChange={(e) => set('referrer_check', e.target.checked)} /> Referer check
                </label>
              </div>

              <Field label="Fraud Guard" help="automatic click-fraud analysis" className="md:col-span-2">
                <div className="pt-1"><Switch checked={!!form.fraud_guard_enabled} onChange={(v) => set('fraud_guard_enabled', v)} labelOn="Enabled" labelOff="Disabled" /></div>
              </Field>
            </div>
          </Card>

          <section aria-label="Audience context"><Card className="p-5 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SectionTitle>Audience context</SectionTitle>
              <p className="md:col-span-2 -mt-2 text-sm text-slate-500">Add page and campaign details to existing events, including visits without an HTTP Referer. Enrichment does not add another pageview.</p>
              <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
                <input type="checkbox" checked={!!form.context_enrichment_enabled} onChange={e => set('context_enrichment_enabled', e.target.checked)} />
                Enable event context enrichment
              </label>
              <fieldset disabled={!form.context_enrichment_enabled} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 disabled:opacity-50">
                <legend className="sr-only">Context fields and query filters</legend>
                <div className="md:col-span-2 flex flex-wrap gap-x-5 gap-y-3">
                  {CONTEXT_OPTIONS.map(option => <label key={option.key} className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={context[option.key]} onChange={e => setContext(option.key, e.target.checked)} />{option.label}
                  </label>)}
                </div>
                <p className="md:col-span-2 text-xs text-slate-500">Page paths and parameter values may contain personal data. Click ID options record presence only. The URL fragment is excluded.</p>
                <Field label="Query parameter mode">
                  <Select aria-label="Query parameter mode" disabled={!context.querystring} value={context.query_mode} onChange={e => setContext('query_mode', e.target.value)}>
                    <option value="allowlist">Only listed parameters</option><option value="all">All except sensitive and excluded parameters</option>
                  </Select>
                </Field>
                <Field label="Allowed parameter names" help="separated by commas or new lines">
                  <Textarea aria-label="Allowed parameter names" rows={2} disabled={!context.querystring || context.query_mode !== 'allowlist'} value={form.context_allowlist_text as string} onChange={e => set('context_allowlist_text', e.target.value)} placeholder="campaign, source" />
                </Field>
                <p className="md:col-span-2 text-xs text-slate-500">Sensitive parameters are always filtered. UTM fields that are switched off and full click IDs are also excluded from the query.</p>
                <Field label="Excluded parameter names" help="additional exclusions, separated by commas or new lines" className="md:col-span-2">
                  <Textarea aria-label="Excluded parameter names" rows={2} disabled={!context.querystring} value={form.context_exclusions_text as string} onChange={e => set('context_exclusions_text', e.target.value)} placeholder="email, token, order_id" />
                </Field>
              </fieldset>
            </div>
          </Card>

          </section>
          <DestinationSetPicker value={form.destination_set_id as string} onChange={(value) => set('destination_set_id', value)} />

          <Card className="p-5 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SectionTitle>First Click Gate</SectionTitle>
              <p className="md:col-span-2 -mt-2 text-xs text-slate-500">
                Controla a entrega pela origem do primeiro toque do usuário (expira em 7 dias → organic). <b>Block</b> tem prioridade; <b>Allow</b>, se marcado, restringe a entrega só às origens marcadas. Vazio = entrega para todos.
              </p>
              <Field label="Allow (só entrega destes)">
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
                  {FIRST_CLICK_CATS.map((c) => (
                    <label key={c.v} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <input type="checkbox" checked={(form.firstclick_allow as string[]).includes(c.v)} onChange={() => toggleFC('firstclick_allow', c.v)} />
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                      {c.label}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Block (nunca entrega)">
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
                  {FIRST_CLICK_CATS.map((c) => (
                    <label key={c.v} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <input type="checkbox" checked={(form.firstclick_block as string[]).includes(c.v)} onChange={() => toggleFC('firstclick_block', c.v)} />
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                      {c.label}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Block specific sources" help="utm_source ou domínio de referrer, separados por vírgula" className="md:col-span-2">
                <Input
                  value={form.firstclick_block_sources as string}
                  onChange={(e) => set('firstclick_block_sources', e.target.value)}
                  placeholder="criteo, taboola, competitor.com"
                />
              </Field>
            </div>
          </Card>
        </>
      ) : (
        <>
        <Card className="p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SectionTitle>Branding Setup</SectionTitle>
            <Field label="Primary Domain" className="md:col-span-2"><Input value={form.primary_domain as string} onChange={(e) => set('primary_domain', e.target.value)} placeholder="example.com" /></Field>
            <Field label="Target URL" className="md:col-span-2"><Input value={form.target_url as string} onChange={(e) => set('target_url', e.target.value)} placeholder="https://example.com/page" /></Field>
            <Field label="Site Type">
              <Select value={form.site_type as string} onChange={(e) => set('site_type', e.target.value)}>
                <option value="">Select</option>
                <option value="ECOMMERCE">E-Commerce</option>
                <option value="IGAMING">iGaming</option>
                <option value="TRAVEL">Travel</option>
                <option value="LANDING">Landing Page</option>
              </Select>
            </Field>
            <Field label="Total Sessions / Day"><Input type="number" min={0} value={form.total_sessions as string} onChange={(e) => set('total_sessions', e.target.value)} /></Field>
            <Field label="Bounce Rate (%)"><Input type="number" min={0} max={100} value={form.bounce_rate as string} onChange={(e) => set('bounce_rate', e.target.value)} /></Field>
            <Field label="Bounce Max Seconds"><Input type="number" min={0} value={form.bounce_max_seconds as string} onChange={(e) => set('bounce_max_seconds', e.target.value)} /></Field>
            <Field label="Session Time (min / p50 / max)" className="md:col-span-2">
              <div className="flex items-center gap-2">
                <Input type="number" placeholder="min" value={form.session_time_min as string} onChange={(e) => set('session_time_min', e.target.value)} />
                <Input type="number" placeholder="p50" value={form.session_time_p50 as string} onChange={(e) => set('session_time_p50', e.target.value)} />
                <Input type="number" placeholder="max" value={form.session_time_max as string} onChange={(e) => set('session_time_max', e.target.value)} />
              </div>
            </Field>
            <Field label="Pages / Session (min / max)" className="md:col-span-2">
              <div className="flex items-center gap-2">
                <Input type="number" placeholder="min" value={form.pages_per_session_min as string} onChange={(e) => set('pages_per_session_min', e.target.value)} />
                <span className="text-slate-300">–</span>
                <Input type="number" placeholder="max" value={form.pages_per_session_max as string} onChange={(e) => set('pages_per_session_max', e.target.value)} />
              </div>
            </Field>
            <Field label="Referers (one per line)" className="md:col-span-4"><Textarea rows={3} value={form.branding_referers as string} onChange={(e) => set('branding_referers', e.target.value)} placeholder="https://google.com/" /></Field>
            <Field label="Session Isolation" className="md:col-span-4">
              <div className="flex flex-wrap gap-4 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!form.isolation_reset_profile} onChange={(e) => set('isolation_reset_profile', e.target.checked)} /> Reset Profile</label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!form.isolation_clear_cookies} onChange={(e) => set('isolation_clear_cookies', e.target.checked)} /> Clear Cookies</label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!form.isolation_incognito} onChange={(e) => set('isolation_incognito', e.target.checked)} /> Incognito</label>
              </div>
            </Field>
          </div>
        </Card>

        <Card className="p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SectionTitle>Pacing Schedule</SectionTitle>
            <Field label="Timezone" className="md:col-span-2">
              <Select value={form.pacing_timezone as string} onChange={(e) => set('pacing_timezone', e.target.value)}>
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </Select>
            </Field>
            <div className="md:col-span-4">
              <PacingGrid value={form.pacing_slots as Slots} onChange={(s) => set('pacing_slots', s)} />
            </div>
          </div>
        </Card>
        </>
      )}

      <Card className="p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SectionTitle>Implementation</SectionTitle>
          {isAdmin && (
            <>
              <Field label="Campaign JS ID" help="Unique ID for the JS implementation. Auto-generated." className="md:col-span-1">
                <Input value={(form.campaign_js_id as string) || ''} readOnly placeholder="Auto-generated on save" className="bg-surface-muted" />
              </Field>
              <CampaignGtmGenerator key={id || 'new'} campaignId={dirtyFields.current.has('campaign_client_id') ? undefined : id} code={(form.campaign_js_extra_content as string) || ''}
                onChange={code => set('campaign_js_extra_content', code)} onBusy={setGeneratingGtm} />
              <Field label="JS Extra Content" help={`Optional extra JavaScript code included in the campaign JS file served at /js/{campaignJsId}.js`} className="md:col-span-4">
                <Textarea
                  aria-label="JS Extra Content"
                  rows={6}
                  value={(form.campaign_js_extra_content as string) || ''}
                  onChange={(e) => set('campaign_js_extra_content', e.target.value)}
                  placeholder={'// Add extra JavaScript code here (optional)\n// This will be injected into the campaign JS file'}
                  className="font-mono text-xs"
                />
              </Field>
            </>
          )}
          <div className="md:col-span-4">
            <ImplementationCode poolId={(form.campaign_id as string) || null} jsId={(form.campaign_js_id as string) || null} brand={brand} />
          </div>
        </div>
      </Card>
    </Layout>
  );
}
